/**
 * 「リポジトリから最新を取得」ウィザードの解析ヘルパー。
 *
 * GitHub リポジトリの tree・コミット日時と、ショットのメディアアイテム・
 * RPP の内容を突き合わせて取得候補（新規 / 更新 / 最新）を組み立てるための
 * 純粋関数群（API 呼び出しは行わない）。RepoFetchModal から使う。
 *
 * 想定するリポジトリ構成（320_JBAC 等の実リポジトリ準拠）:
 * - ルート直下にショット名のフォルダ（例: `320_JBAC/`）
 * - フォルダ内に `.rpp`、動画（`Movie/` 等）、書き出し音声（`Render/` / `Mixdown/`）
 * - RPP のマーカー / リージョン名が書き出し音声のファイル名（拡張子なし）と一致
 */

import type { GitHubTreeEntry } from '../../../app/services/github';
import type { MediaItem } from '../../../app/services/mediaItems';
import type { MaParseResult } from './maTypes';
import { canonicalMediaKey, stripExtension } from './maTypes';

// ===== ファイル種別 =====

export type RepoMediaKind = 'video' | 'audio';

const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|avi)$/i;
const AUDIO_EXT_RE = /\.(wav|mp3|aif|aiff|flac|ogg|m4a|aac)$/i;
const RPP_EXT_RE = /\.rpp$/i;

/** パス末尾のファイル名 */
export const repoBaseName = (path: string): string => path.split('/').pop() ?? path;

/** 拡張子からメディア種別を判定（対象外は null） */
export function repoMediaKind(fileName: string): RepoMediaKind | null {
  if (VIDEO_EXT_RE.test(fileName)) return 'video';
  if (AUDIO_EXT_RE.test(fileName)) return 'audio';
  return null;
}

export const isRppPath = (path: string): boolean => RPP_EXT_RE.test(path);

// ===== ショットフォルダ探索 =====

/**
 * ルート直下のディレクトリからショット名に対応するフォルダ候補を探す。
 * 完全一致 > 前方一致（どちらかがもう一方で始まる）の順に返す。
 * 誤爆を避けるため、前方一致はフォルダ名 3 文字以上のみ対象。
 */
export function findShotFolderCandidates(
  entries: GitHubTreeEntry[],
  shotName: string,
): string[] {
  const canonShot = canonicalMediaKey(shotName.trim());
  const scored: { path: string; score: number }[] = [];
  for (const entry of entries) {
    if (entry.type !== 'dir') continue;
    const canonDir = canonicalMediaKey(repoBaseName(entry.path));
    if (!canonDir) continue;
    let score = 0;
    if (canonDir === canonShot) {
      score = 3;
    } else if (canonDir.length >= 3 && canonShot.startsWith(canonDir)) {
      // 例: ショット「320_JBAC_○○」に対しフォルダ「320_JBAC」
      score = 2;
    } else if (canonShot.length >= 3 && canonDir.startsWith(canonShot)) {
      score = 1;
    }
    if (score > 0) scored.push({ path: entry.path, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.path.localeCompare(b.path))
    .map((s) => s.path);
}

// ===== RPP 内メディア名の収集 =====

/**
 * RPP パース結果から照合対象のメディア名を集める（canonical key → 原表記）。
 * 本編行に加え、オプション候補（`[x]` パレット領域）の行も含める。
 */
export function collectRppMediaNames(parsed: MaParseResult): Map<string, string> {
  const map = new Map<string, string>();
  const add = (media: string) => {
    const key = canonicalMediaKey(media.trim());
    if (key && !map.has(key)) map.set(key, media.trim());
  };
  parsed.rows.forEach((row) => add(row.media));
  parsed.optionSlots.forEach((slot) =>
    slot.choices.forEach((choice) => choice.rows.forEach((row) => add(row.media))),
  );
  return map;
}

// ===== リポジトリファイルとの照合 =====

/** リポジトリ内で RPP のメディア名と一致したファイル */
export interface RepoFileMatch {
  /** リポジトリ内フルパス */
  path: string;
  fileName: string;
  /** RPP 内の原表記（表示・照合キーの元） */
  mediaName: string;
  canonicalKey: string;
  kind: RepoMediaKind;
}

/**
 * 同名ファイルが複数あるときの優先度（小さいほど優先）。
 * 書き出し音声の置き場である Render/ > Mixdown/ を優先し、
 * 同格なら浅いパスを選ぶ。
 */
const folderPriority = (path: string): number => {
  const depth = path.split('/').length;
  if (/(^|\/)render(\/|$)/i.test(path)) return depth;
  if (/(^|\/)mixdown(\/|$)/i.test(path)) return 1000 + depth;
  return 2000 + depth;
};

/**
 * フォルダ配下のファイル一覧を RPP 内メディア名と照合する。
 * 返り値の matches は mediaNames の登場順、missingNames は未発見分。
 */
export function matchRepoFiles(
  files: GitHubTreeEntry[],
  mediaNames: Map<string, string>,
): { matches: RepoFileMatch[]; missingNames: Map<string, string> } {
  const best = new Map<string, { match: RepoFileMatch; priority: number }>();
  for (const file of files) {
    if (file.type !== 'file') continue;
    const fileName = repoBaseName(file.path);
    const kind = repoMediaKind(fileName);
    if (!kind) continue;
    const key = canonicalMediaKey(stripExtension(fileName));
    const mediaName = mediaNames.get(key);
    if (mediaName === undefined) continue;
    const priority = folderPriority(file.path);
    const current = best.get(key);
    if (!current || priority < current.priority) {
      best.set(key, {
        match: { path: file.path, fileName, mediaName, canonicalKey: key, kind },
        priority,
      });
    }
  }

  const matches: RepoFileMatch[] = [];
  const missingNames = new Map<string, string>();
  for (const [key, name] of mediaNames) {
    const found = best.get(key);
    if (found) {
      matches.push(found.match);
    } else {
      missingNames.set(key, name);
    }
  }
  return { matches, missingNames };
}

/** 他ショットフォルダで見つかった不足メディア */
export interface CrossShotMatch extends RepoFileMatch {
  /** 探索スコープ直下のフォルダ名（= 参照先ショット名の候補） */
  folderName: string;
}

/**
 * 自ショットフォルダに無いメディアを、他フォルダの書き出し置き場
 * （Render/ / Mixdown/ 配下）から探す（② 他ショット参照の候補）。
 *
 * ショットフォルダが「カテゴリ/ショット名」のように入れ子の場合は、
 * scopePrefix に親フォルダ（カテゴリ）を渡す。folderName は scopePrefix
 * 直下のフォルダ名（= 兄弟ショットフォルダ名）になる。
 */
export function findCrossShotMatches(
  allFiles: GitHubTreeEntry[],
  ownFolder: string,
  missingNames: Map<string, string>,
  scopePrefix = '',
): CrossShotMatch[] {
  if (missingNames.size === 0) return [];
  const ownPrefix = `${ownFolder}/`;
  const prefix = scopePrefix ? `${scopePrefix}/` : '';
  const best = new Map<string, { match: CrossShotMatch; priority: number }>();
  for (const file of allFiles) {
    if (file.type !== 'file') continue;
    if (file.path.startsWith(ownPrefix)) continue;
    if (prefix && !file.path.startsWith(prefix)) continue;
    // 書き出し音声の置き場のみ対象（作業ファイル等の誤検出を防ぐ）
    if (!/(^|\/)(render|mixdown)(\/|$)/i.test(file.path)) continue;
    const fileName = repoBaseName(file.path);
    const kind = repoMediaKind(fileName);
    if (!kind) continue;
    const key = canonicalMediaKey(stripExtension(fileName));
    const mediaName = missingNames.get(key);
    if (mediaName === undefined) continue;
    const priority = folderPriority(file.path);
    const current = best.get(key);
    if (!current || priority < current.priority) {
      best.set(key, {
        match: {
          path: file.path,
          fileName,
          mediaName,
          canonicalKey: key,
          kind,
          folderName: file.path.slice(prefix.length).split('/')[0],
        },
        priority,
      });
    }
  }
  // missingNames の登場順で返す
  const result: CrossShotMatch[] = [];
  for (const key of missingNames.keys()) {
    const found = best.get(key);
    if (found) result.push(found.match);
  }
  return result;
}

// ===== 更新判定 =====

/**
 * ショットのメディアアイテムを照合キー（バリエーション名 / ファイル名ステム）で
 * 引けるようにする。同キーに複数テイクがあれば updated_at 最新を採用。
 */
export function buildShotItemIndex(items: MediaItem[]): Map<string, MediaItem> {
  const map = new Map<string, MediaItem>();
  const put = (rawKey: string, item: MediaItem) => {
    const key = canonicalMediaKey(rawKey);
    if (!key) return;
    const current = map.get(key);
    if (
      !current ||
      new Date(item.updated_at).getTime() > new Date(current.updated_at).getTime()
    ) {
      map.set(key, item);
    }
  };
  for (const item of items) {
    put(item.variation_name ?? '', item);
    put(stripExtension(item.file_name ?? ''), item);
  }
  return map;
}

/**
 * コミット日時が既存アイテムの更新日時より新しいか。
 * 既存アイテムが無い / コミット日時が取れない場合は true（取得候補にする）。
 */
export function isCommitNewer(
  commitDate: string | null | undefined,
  item: MediaItem | undefined,
): boolean {
  if (!item) return true;
  if (!commitDate) return true;
  return new Date(commitDate).getTime() > new Date(item.updated_at).getTime();
}

/**
 * リポジトリのフォルダ名からプロジェクト内ショットを探す
 * （findShotFolderCandidates と同じ許容度の照合を逆向きに行う）。
 */
export function findShotByFolderName<T extends { id: string; shot_name: string }>(
  shots: T[],
  folderName: string,
): T | null {
  const canonFolder = canonicalMediaKey(folderName.trim());
  if (!canonFolder) return null;
  let bestShot: T | null = null;
  let bestScore = 0;
  for (const shot of shots) {
    const canonShot = canonicalMediaKey(shot.shot_name.trim());
    if (!canonShot) continue;
    let score = 0;
    if (canonShot === canonFolder) {
      score = 3;
    } else if (canonFolder.length >= 3 && canonShot.startsWith(canonFolder)) {
      score = 2;
    } else if (canonShot.length >= 3 && canonFolder.startsWith(canonShot)) {
      score = 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestShot = shot;
    }
  }
  return bestShot;
}
